import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';
import { resolveTenantSlug } from './tenant';
import type { PublicBranding } from './types';

/** Mirrors the platform defaults in app/shared/src/branding.ts: the two self-hosted faces and square corners. */
const DEFAULT_UI_FONT = { key: 'instrument-sans', family: '"Instrument Sans Variable", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };
const DEFAULT_DISPLAY_FONT = { key: 'instrument-serif', family: '"Instrument Serif", ui-serif, Georgia, "Times New Roman", serif' };

const FALLBACK: PublicBranding = {
  tenantSlug: resolveTenantSlug(),
  tenantName: 'Crewline',
  appName: 'Crewline',
  tagline: 'Reach everyone on the job.',
  colors: {},
  darkColors: {},
  loginMethods: { password: true, emailCode: false, smsCode: false, sso: { mode: 'off', providers: [] } },
  fonts: { ui: DEFAULT_UI_FONT, display: DEFAULT_DISPLAY_FONT },
  radius: 0,
  signIn: { title: 'Sign in', intro: 'Compose once, reach the whole crew, know exactly who received it.', footerNote: '', showNotes: true, showSkyline: true },
  legal: { privacyUrl: null, termsUrl: null, signupUrl: null, optOutUrl: null },
};

/** Branding color keys → CSS variables (see src/index.css). */
const VAR_MAP: Record<string, string[]> = {
  primary: ['--primary', '--sidebar-primary', '--ring', '--sidebar-ring', '--chart-1'],
  primaryForeground: ['--primary-foreground', '--sidebar-primary-foreground'],
  accent: ['--accent', '--sidebar-accent'],
  accentForeground: ['--accent-foreground', '--sidebar-accent-foreground'],
  background: ['--background'],
  foreground: ['--foreground'],
  card: ['--card'],
  cardForeground: ['--card-foreground'],
  muted: ['--muted'],
  mutedForeground: ['--muted-foreground'],
  border: ['--border', '--input', '--sidebar-border'],
  ring: ['--ring', '--sidebar-ring'],
  destructive: ['--destructive'],
  sidebar: ['--sidebar'],
  sidebarForeground: ['--sidebar-foreground'],
  popover: ['--popover'],
  popoverForeground: ['--popover-foreground'],
  secondary: ['--secondary'],
  secondaryForeground: ['--secondary-foreground'],
  // Sign-in page hero/header (see layouts/AuthLayout.tsx)
  brandPanel: ['--brand-panel'],
  brandPanelForeground: ['--brand-panel-foreground'],
  brandAccent: ['--brand-accent'],
};

function cssBlock(selector: string, colors: Record<string, string>): string {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(colors)) {
    const vars = VAR_MAP[key];
    if (!vars || !/^[#a-z0-9(),.%\s/-]+$/i.test(value)) continue;
    for (const v of vars) lines.push(`  ${v}: ${value};`);
  }
  return lines.length ? `${selector} {\n${lines.join('\n')}\n}` : '';
}

const FONT_FAMILY_RE = /^[\w\s"',-]+$/;

/** Typeface stacks and the corner radius as CSS variables (index.css maps them onto Tailwind's font and radius tokens). */
function styleBlock(b: PublicBranding): string {
  const lines: string[] = [];
  const ui = b.fonts?.ui?.family;
  const display = b.fonts?.display?.family;
  if (ui && FONT_FAMILY_RE.test(ui)) lines.push(`  --brand-font-sans: ${ui};`);
  if (display && FONT_FAMILY_RE.test(display)) lines.push(`  --brand-font-display: ${display};`);
  const radius = Number.isFinite(b.radius) ? Math.min(16, Math.max(0, Math.round(b.radius))) : 0;
  lines.push(`  --radius: ${radius}px;`);
  return `html:root {\n${lines.join('\n')}\n}`;
}

/** Load the Google Fonts faces the tenant chose; the two defaults are bundled with the app. */
function ensureGoogleFonts(b: PublicBranding): void {
  const specs = [...new Set([b.fonts?.ui?.google, b.fonts?.display?.google].filter((s): s is string => typeof s === 'string' && /^[A-Za-z0-9+:;,.@]+$/.test(s)))];
  const id = 'tenant-fonts';
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  if (specs.length === 0) {
    existing?.remove();
    return;
  }
  const href = `https://fonts.googleapis.com/css2?${specs.map((s) => `family=${s}`).join('&')}&display=swap`;
  if (existing?.href === href) return;
  const link = existing ?? document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  if (!link.parentNode) document.head.appendChild(link);
}

/** Injects tenant tokens after the base stylesheet; `html:root` / `html.dark` beat `:root` / `.dark`. */
export function applyBranding(b: PublicBranding): void {
  const id = 'tenant-branding';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = [styleBlock(b), cssBlock('html:root', b.colors ?? {}), cssBlock('html.dark', b.darkColors ?? {})].filter(Boolean).join('\n');
  ensureGoogleFonts(b);
  document.title = b.appName;
  const link = (document.getElementById('favicon') as HTMLLinkElement | null) ?? document.createElement('link');
  link.id = 'favicon';
  link.rel = 'icon';
  link.href = b.faviconUrl || '/favicon.svg';
  if (!link.parentNode) document.head.appendChild(link);
}

interface BrandingContextValue {
  branding: PublicBranding;
  setBranding: (b: PublicBranding) => void;
  loaded: boolean;
  error: string | null;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBrandingState] = useState<PublicBranding>(FALLBACK);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.tenant
      .branding()
      .then((b) => {
        if (cancelled) return;
        setBrandingState(b);
        applyBranding(b);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load branding');
        applyBranding(FALLBACK);
      })
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Stable identity: the auth provider keeps this in effect dependencies. A fresh function on every
  // branding change re-ran the boot refresh each time /auth/me reported branding, rotating the session in a loop.
  const setBranding = useCallback((b: PublicBranding) => {
    setBrandingState(b);
    applyBranding(b);
  }, []);

  const value = useMemo<BrandingContextValue>(() => ({ branding, loaded, error, setBranding }), [branding, loaded, error, setBranding]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}
